import React, { useState, useLayoutEffect, ReactNode } from "react";
import { createPortal } from "react-dom";

// Multiple ReactPortal instances can share one wrapperId (every SlidePanel
// targets "slide-panel-portal") and can be mounted concurrently — e.g. one
// panel still finishing its slide-out exit transition while another opens.
// Ref-count consumers per wrapperId so the DOM node is only removed once the
// LAST consumer unmounts, not whichever instance happened to create it.
const wrapperRegistry = new Map<
  string,
  { refCount: number; createdByUs: boolean }
>();

const ReactPortal: React.FC<{
  wrapperId?: string;
  children?: ReactNode | undefined;
}> = ({ children, wrapperId = "react-portal-wrapper" }) => {
  const [wrapperElement, setWrapperElement] = useState<HTMLElement | null>(
    null,
  );

  useLayoutEffect(() => {
    let element = document.getElementById(wrapperId);
    const entry = wrapperRegistry.get(wrapperId) ?? {
      refCount: 0,
      createdByUs: false,
    };
    // if element is not found with wrapperId or wrapperId is not provided,
    // create and append to body
    if (!element) {
      element = createWrapperAndAppendToBody(wrapperId);
      entry.createdByUs = true;
    }
    entry.refCount += 1;
    wrapperRegistry.set(wrapperId, entry);
    setWrapperElement(element);

    return () => {
      const current = wrapperRegistry.get(wrapperId);
      if (!current) return;
      current.refCount -= 1;
      if (current.refCount > 0) return;
      wrapperRegistry.delete(wrapperId);
      // delete the programmatically created element, only once every consumer is gone
      if (current.createdByUs) {
        const el = document.getElementById(wrapperId);
        if (el?.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    };
  }, [wrapperId]);

  // wrapperElement state will be null on the very first render.
  if (wrapperElement === null) return null;

  return createPortal(children, wrapperElement);
};

const createWrapperAndAppendToBody = (wrapperId: string) => {
  const wrapperElement = document.createElement("div");
  wrapperElement.setAttribute("id", wrapperId);
  document.body.appendChild(wrapperElement);
  return wrapperElement;
};

export default ReactPortal;
